import React from "react";
import LegalDocumentLayout from "../components/LegalDocumentLayout";

function PrivacyPolicy() {
  return (
    <LegalDocumentLayout
      title="Privacy Policy"
      lastUpdated="Last updated Feb 3, 2023"
    >
      <p>
        Thank you for choosing to be part of our community at Fiero Inc
        (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;).
        We are committed to protecting your personal information and your right
        to privacy. If you have any questions or concerns about our policy, or
        our practices with regards to your personal information, please contact
        us at{" "}
        <a
          href="mailto:hello@fierolearning.com"
          className="font-medium text-primary-600 underline hover:text-primary-800"
        >
          hello@fierolearning.com
        </a>
        .
      </p>

      <p>
        When you visit our website{" "}
        <a
          href="https://app.fierocode.com"
          className="font-medium text-primary-600 underline hover:text-primary-800"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://app.fierocode.com
        </a>
        , and use our services, you trust us with your personal information. We
        take your privacy very seriously. In this privacy notice, we describe
        our privacy policy. We seek to explain to you in the clearest way
        possible what information we collect, how we use it and what rights you
        have in relation to it. We hope you take some time to read through it
        carefully, as it is important. If there are any terms in this privacy
        policy that you do not agree with, please discontinue use of our Sites
        and our services.
      </p>

      <p>
        This privacy policy applies to all information collected through our
        website (such as{" "}
        <a
          href="https://app.fierocode.com"
          className="font-medium text-primary-600 underline hover:text-primary-800"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://app.fierocode.com
        </a>
        ), and/or any related services, sales, marketing or events (we refer to
        them collectively in this privacy policy as the &quot;Sites&quot;).
      </p>

      <p>
        Please read this privacy policy carefully as it will help you make
        informed decisions about sharing your personal information with us.
      </p>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          Information Collection And Use
        </h2>
        <p className="mt-4">
          We collect several different types of information for various
          purposes to provide and improve our Service to you.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-gray-900">
          Types of Data Collected
        </h3>

        <h4 className="mt-6 font-semibold text-gray-900">Personal Data</h4>
        <p className="mt-3">
          While using our Service, we may ask you to provide us with certain
          personally identifiable information that can be used to contact or
          identify you (&quot;Personal Data&quot;). Personally identifiable
          information may include, but is not limited to:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>Parent Email address (if under 13 years old)</li>
          <li>Email address (if over 13 years old)</li>
          <li>First name</li>
          <li>Gender</li>
          <li>Birth Month &amp; Year</li>
          <li>Accessing institution name</li>
          <li>Cookies and Usage Data</li>
        </ul>
        <p className="mt-4">
          We may use your Personal Data to contact you with newsletters,
          marketing or promotional materials and other information that may be
          of interest to you. You may opt out of receiving any, or all, of these
          communications from us by following the unsubscribe link or
          instructions provided in any email we send.
        </p>

        <h4 className="mt-8 font-semibold text-gray-900">Usage Data</h4>
        <p className="mt-3">
          We may also collect information how the Service is accessed and used
          (&quot;Usage Data&quot;). This Usage Data may include information such
          as your computer&apos;s Internet Protocol address (e.g. IP address),
          browser type, browser version, the pages of our Service that you
          visit, the time and date of your visit, the time spent on those pages,
          unique device identifiers and other diagnostic data. App specific
          usage data includes but is not limited to:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>Logins</li>
          <li>Coding lessons completed</li>
          <li>Time spent in the app</li>
        </ul>

        <h4 className="mt-8 font-semibold text-gray-900">
          Tracking &amp; Cookies Data
        </h4>
        <p className="mt-3">
          We use cookies and similar tracking technologies to track the activity
          on our Service and hold certain information.
        </p>
        <p className="mt-4">
          Cookies are files with small amount of data which may include an
          anonymous unique identifier. Cookies are sent to your browser from a
          website and stored on your device. Tracking technologies also used are
          beacons, tags, and scripts to collect and track information and to
          improve and analyze our Service.
        </p>
        <p className="mt-4">
          You can instruct your browser to refuse all cookies or to indicate
          when a cookie is being sent. However, if you do not accept cookies, you
          may not be able to use some portions of our Service.
        </p>
        <p className="mt-4 font-medium">Examples of Cookies we use:</p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>
            <strong>Session Cookies.</strong> We use Session Cookies to operate
            our Service.
          </li>
          <li>
            <strong>Preference Cookies.</strong> We use Preference Cookies to
            remember your preferences and various settings.
          </li>
          <li>
            <strong>Security Cookies.</strong> We use Security Cookies for
            security purposes.
          </li>
        </ul>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          How Do We Collect Your Data
        </h2>
        <p className="mt-4">You directly provide Fiero Code your data when you:</p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>Register as a new user</li>
          <li>If accessing through Google OAuth/ Google Classroom</li>
          <li>View or use our website via your browser&apos;s cookies</li>
        </ul>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">Use of Data</h2>
        <p className="mt-4">
          Fiero uses the collected data for various purposes:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>To provide and maintain our Service</li>
          <li>To notify you about changes to our Service</li>
          <li>
            To allow you to participate in interactive features of our Service
            when you choose to do so
          </li>
          <li>To provide customer support</li>
          <li>
            To gather analysis or valuable information so that we can improve
            our Service
          </li>
          <li>To monitor the usage of our Service</li>
          <li>To detect, prevent and address technical issues</li>
          <li>
            To provide you with news, special offers and general information
            about other goods, services and events which we offer that are
            similar to those that you have already purchased or enquired about
            unless you have opted not to receive such information
          </li>
        </ul>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">Limited Use</h2>
        <p className="mt-4">
          Fiero Code&apos;s use of information received from Google APIs will
          adhere to the Google API Services User Data Policy, including the
          Limited Use requirements.
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">Transfer Of Data</h2>
        <p className="mt-4">
          Your information, including Personal Data, may be transferred to —
          and maintained on — computers located outside of your state,
          province, country or other governmental jurisdiction where the data
          protection laws may differ than those from your jurisdiction.
        </p>
        <p className="mt-4">
          If you are located outside United States and choose to provide
          information to us, please note that we transfer the data, including
          Personal Data, to United States and process it there.
        </p>
        <p className="mt-4">
          Your consent to this Privacy Policy followed by your submission of
          such information represents your agreement to that transfer.
        </p>
        <p className="mt-4">
          Fiero will take all steps reasonably necessary to ensure that your
          data is treated securely and in accordance with this Privacy Policy
          and no transfer of your Personal Data will take place to an
          organization or a country unless there are adequate controls in place
          including the security of your data and other personal information.
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          Disclosure Of Data
        </h2>

        <h3 className="mt-6 text-lg font-semibold text-gray-900">
          Business Transaction
        </h3>
        <p className="mt-3">
          If Fiero is involved in a merger, acquisition or asset sale, your
          Personal Data may be transferred. We will provide notice before your
          Personal Data is transferred and becomes subject to a different
          Privacy Policy.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-gray-900">
          Disclosure for Law Enforcement
        </h3>
        <p className="mt-3">
          Under certain circumstances, Fiero may be required to disclose your
          Personal Data if required to do so by law or in response to valid
          requests by public authorities (e.g. a court or a government agency).
        </p>

        <h3 className="mt-8 text-lg font-semibold text-gray-900">
          Legal Requirements
        </h3>
        <p className="mt-3">
          Fiero may disclose your Personal Data in the good faith belief that
          such action is necessary to:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>To comply with a legal obligation</li>
          <li>To protect and defend the rights or property of Fiero</li>
          <li>
            To prevent or investigate possible wrongdoing in connection with the
            Service
          </li>
          <li>
            To protect the personal safety of users of the Service or the
            public
          </li>
          <li>To protect against legal liability</li>
        </ul>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">Security Of Data</h2>
        <p className="mt-4">
          The security of your data is important to us, but remember that no
          method of transmission over the Internet, or method of electronic
          storage is 100% secure. While we strive to use commercially acceptable
          means to protect your Personal Data, we cannot guarantee its absolute
          security.
        </p>
        <p className="mt-4">
          Fiero&apos;s database provider utilizes the most demanding of data
          security and privacy standards. Internal policies to keep data secure
          include limiting access to the database, encrypting backups, masking
          and encrypting data in development environments, and limiting access to
          data by all user roles to read-only.
        </p>
        <p className="mt-4">
          Your data is stored on servers located in the United States of
          America, and we will keep your data indefinitely, unless you request
          us to delete your data.
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          Your Data Protection Rights
        </h2>
        <p className="mt-4">
          Fiero would like to make sure you are fully aware of all your data
          protection rights. Every user is entitled to the following:
        </p>
        <ul className="mt-4 list-disc space-y-3 pl-6">
          <li>
            <strong>The right to access</strong> — You have the right to request
            Fiero for copies of your personal data. We may charge you a small
            fee for this service.
          </li>
          <li>
            <strong>The right to rectification</strong> — You have the right to
            request that Fiero correct any information you believe in
            inaccurate. You also have the right to request Fiero to complete
            information you believe is incomplete.
          </li>
          <li>
            <strong>The right to erasure</strong> — You have the right to
            request that Fiero restrict the processing of your personal data,
            under certain conditions.
          </li>
          <li>
            <strong>The right to restrict processing</strong> — You have the
            right to request that Fiero restrict the processing of your personal
            data, under certain conditions.
          </li>
          <li>
            <strong>The right to object to processing</strong> — You have the
            right to object to Fiero&apos;s processing of your personal data,
            under certain conditions.
          </li>
          <li>
            <strong>The right to data portability</strong> — You have the right
            to request that Fiero transfer the data that we have collected to
            another organization, or directly to you, under certain conditions.
          </li>
        </ul>
        <p className="mt-6">
          If you make a request, we have one month to respond to you. If you
          would like to exercise any of these rights, please contact us at our
          email:{" "}
          <a
            href="mailto:hello@fierolearning.com"
            className="font-medium text-primary-600 underline hover:text-primary-800"
          >
            hello@fierolearning.com
          </a>
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">Service Providers</h2>
        <p className="mt-4">
          We may employ third party companies and individuals to facilitate our
          Service (&quot;Service Providers&quot;), to provide the Service on
          our behalf, to perform Service-related services or to assist us in
          analyzing how our Service is used.
        </p>
        <p className="mt-4">
          These third parties have access to your Personal Data only to perform
          these tasks on our behalf and are obligated not to disclose or use it
          for any other purpose.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-gray-900">Analytics</h3>
        <p className="mt-3">
          We may use third-party Service Providers to monitor and analyze the
          use of our Service.
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>
            <strong>Google Analytics</strong>
          </li>
        </ul>
        <p className="mt-4">
          Google Analytics is a web analytics service offered by Google that
          tracks and reports website traffic. Google uses the data collected to
          track and monitor the use of our Service. This data is shared with
          other Google services. Google may use the collected data to
          contextualize and personalize the ads of its own advertising network.
        </p>
        <p className="mt-4">
          You can opt-out of having made your activity on the Service available
          to Google Analytics by installing the Google Analytics opt-out browser
          add-on. The add-on prevents the Google Analytics JavaScript (ga.js,
          analytics.js, and dc.js) from sharing information with Google
          Analytics about visits activity.
        </p>
        <p className="mt-4">
          For more information on the privacy practices of Google, please visit
          the Google Privacy &amp; Terms web page:{" "}
          <a
            href="http://www.google.com/intl/en/policies/privacy/"
            className="break-all font-medium text-primary-600 underline hover:text-primary-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            http://www.google.com/intl/en/policies/privacy/
          </a>
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          Links To Other Sites
        </h2>
        <p className="mt-4">
          Our Service may contain links to other sites that are not operated by
          us. If you click on a third party link, you will be directed to that
          third party&apos;s site. We strongly advise you to review the Privacy
          Policy of every site you visit.
        </p>
        <p className="mt-4">
          We have no control over and assume no responsibility for the content,
          privacy policies or practices of any third party sites or services.
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          Children&apos;s Privacy
        </h2>
        <p className="mt-4">
          Our Service does not address anyone under the age of 13
          (&quot;Children&quot;).
        </p>
        <p className="mt-4">
          We do not knowingly collect personally identifiable information from
          anyone under the age of 13. If you are a parent or guardian and you
          are aware that your Children has provided us with Personal Data,
          please contact us. If we become aware that we have collected Personal
          Data from children without verification of parental consent, we take
          steps to remove that information from our servers.
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">
          Changes To This Privacy Policy
        </h2>
        <p className="mt-4">
          We may update our Privacy Policy from time to time. We will notify you
          of any changes by posting the new Privacy Policy on this page.
        </p>
        <p className="mt-4">
          We will let you know via email and a prominent notice on our Service,
          prior to the change becoming effective and update the &quot;effective
          date&quot; at the top of this Privacy Policy.
        </p>
        <p className="mt-4">
          You are advised to review this Privacy Policy periodically for any
          changes. Changes to this Privacy Policy are effective when they are
          posted on this page.
        </p>
      </section>

      <section className="scroll-mt-24">
        <h2 className="text-xl font-bold text-primary-500">Contact Us</h2>
        <p className="mt-4">
          If you have any questions about this Privacy Policy, please contact
          us:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-6">
          <li>
            By email:{" "}
            <a
              href="mailto:hello@fierolearning.com"
              className="font-medium text-primary-600 underline hover:text-primary-800"
            >
              hello@fierolearning.com
            </a>
          </li>
        </ul>
      </section>
    </LegalDocumentLayout>
  );
}

export default PrivacyPolicy;
